import React, { forwardRef } from "react";
import { EntityCard, EntityCardRef, EntityCardProps, BaseEntity } from "components/Entities/EntityCardBase";
import { Chip } from "ui/Chip";
import { Impact } from "util/constants";
import { CircleCheck } from "lucide-react";
import PostKindChip from "components/Posts/display/PostKindChip";
import PostMediaPreview from "components/Posts/display/PostMediaPreview";
import PostReferenceLink from "components/Posts/display/PostReferenceLink";

interface TeabateCardProps extends Omit<EntityCardProps<Impact>, "entity" | "entityType" | "renderTags" | "renderAdditionalContent" | "renderEditFields" | "renderActions" | "renderPrimaryContent"> {
  // Teabate posts (`posts.kind = 'teabate'`). The `Impact` TS shape covers
  // both impacts and teabates because both kinds share the unified-posts
  // row layout; the runtime kind is carried on `teabate.kind`.
  teabate: Impact;
  onPrivacyChange?: (value: string) => void;
  onCardClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * TeabateCard using the generic EntityCard component
 * Teabates are a special type of impact that don't show descriptions and have unique styling
 */
const TeabateCard = forwardRef<EntityCardRef, TeabateCardProps>(
  (props, ref) => {
    const { teabate, onPrivacyChange, onCardClick, ...restProps } = props;
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN || "";
    
    // Default card click handler
    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const element = event.target as HTMLElement;
      if (element.tagName.toLowerCase() === 'a') return;    
      if (element.classList.contains('read-more')) return;    
      if (!props.isEntityProfilePage) {
        event.stopPropagation();
        // Navigate will be handled by EntityCard
      } else {
        event.preventDefault();            
        if (element.tagName.toLowerCase() === 'img' || 
            element.tagName.toLowerCase() === 'video' ||
            element.tagName.toLowerCase() === 'svg' || 
            element.tagName.toLowerCase() === 'path' || 
            element.classList.contains('no-metadata-link')) {
          event.stopPropagation();
          if (teabate.url) {
            window.open(teabate.url, '_blank', 'noopener,noreferrer');
          }
        }
      }
      
      if (onCardClick) {
        onCardClick(event);
      }
    };
    
    // Helper function to check if title should be hidden
    const shouldHideTitle = (title?: string): boolean => {
      return !title || title.trim() === '' || title.trim() === '[no_title]';
    };
    
    // Helper function to get dimension display info
    const getDimensionInfo = (teabate: Impact): { label: string; key: string } | null => {
      if (!teabate.properties?.dimension || teabate.properties.dimension === 'none') {
        return null;
      }
      
      // The dimension is now stored directly as the label (e.g., "nationalism", "cosmopolitanism")
      const dimension = teabate.properties.dimension;
      
      // Capitalize first letter for display
      const label = dimension.charAt(0).toUpperCase() + dimension.slice(1);
      
      return { label, key: dimension };
    };

    // Render teabate-specific tags
    const renderTags = (entity: BaseEntity) => {
      const teabate = entity as Impact;
      const dimensionInfo = getDimensionInfo(teabate);
      
      return (
        <>
          <PostKindChip
            kind="teabate"
            style={{
              backgroundColor: "#3e838a",
              color: "#fff",
            }}
          />
          {dimensionInfo && (
            <Chip
              label={dimensionInfo.label}
              variant="custom"
              size="sm"
              className="bg-eoe-icon dark:bg-eoe-dark-icon text-eoe-text dark:text-eoe-dark-text"
              icon={<CircleCheck className="size-4" />}
            />
          )}
          {teabate.properties?.scope && (
            <Chip
              label={teabate.properties.scope}
              variant="custom"
              size="sm"
              style={{
                backgroundColor: "#555555",
                color: "#fff",
              }}
            />
          )}
        </>
      );
    };

    // Render teabate-specific additional content
    const renderAdditionalContent = (entity: BaseEntity) => {
      const teabate = entity as Impact;
      // Show reference link for teabates if they have one
      if (teabate.url) {
        return (
          <div className="w-full mb-2">
            <PostReferenceLink
              url={teabate.url}
              textClassName="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub"
            />
          </div>
        );
      }
      return null;
    };

    // Render teabate-specific edit fields
    const renderEditFields = (entity: BaseEntity) => {
      const teabate = entity as Impact;
      // Show reference link (read-only) - teabates don't edit their reference links
      if (teabate.url) {
        return (
          <div className="mb-4 md:col-span-3">
            <label className="block text-sm font-medium text-eoe-text dark:text-eoe-dark-text mb-1">
              Reference Link
            </label>
            <PostReferenceLink
              url={teabate.url}
              textClassName="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
            />
          </div>
        );
      }
      // Note: Teabates don't edit descriptions, so we don't override the description editor
      return null;
    };

    // Custom render actions for teabate thumbnails/embeds
    const renderActions = (entity: BaseEntity) => {
      const teabate = entity as Impact;
      
      // If teabate has a reference link, handle social embeds and thumbnails
      if (teabate.url || teabate.properties?.reference_link_img_key || teabate.properties?.reference_link_image_url) {
        return (
          <PostMediaPreview
            url={teabate.url}
            referenceImageKey={teabate.properties?.reference_link_img_key}
            referenceImageUrl={teabate.properties?.reference_link_image_url}
            imageCdn={IMAGE_CDN}
            isProfilePage={props.isEntityProfilePage || false}
            onCardClick={handleCardClick}
          />
        );
      }
      
      // For teabates without reference links and no media, show nothing
      return null;
    };

    return (
      <EntityCard
        ref={ref}
        entity={teabate}
        entityType="teabate"
        onPrivacyChange={onPrivacyChange}
        renderTags={renderTags}
        renderAdditionalContent={renderAdditionalContent}
        renderEditFields={renderEditFields}
        renderActions={renderActions}
        hideTitle={shouldHideTitle(teabate.title)}
        descriptionPlaceholder="Arguments"
        canEditPrivacy={true}
        canEditTitle={false}
        {...restProps}
      />
    );
  }
);

TeabateCard.displayName = "TeabateCard";

export default TeabateCard;
